/** Shared domain vocabulary. Section refs are to the Obsidian vault. */

/**
 * Channels the platform can speak on.
 *
 * Public comments are their own channels, not a flavour of the DM they often
 * turn into. Two differences, and both of them bite:
 *
 * 1. A comment is public. Everyone reading the post sees the reply, so the
 *    register is different and nothing private belongs in one.
 * 2. A comment is not under Meta's 24h messaging window. The window governs
 *    Direct and Messenger conversations; replying to a comment on your own
 *    post is not a message. Filed as `instagram` they would be gated as DMs,
 *    which refuses a reply that is allowed (F-31).
 */
export type Channel =
  | "instagram"
  | "facebook"
  | "instagram_comment"
  | "facebook_comment"
  | "sms"
  | "email"
  | "webchat";

/** Meta-owned rails. Says who owns the pipe, not what may be sent down it. */
export const META_CHANNELS = [
  "instagram",
  "facebook",
  "instagram_comment",
  "facebook_comment",
] as const satisfies readonly Channel[];

/** Meta rails carrying a conversation, which is what the 24h window governs. */
export const WINDOW_GATED_CHANNELS = ["instagram", "facebook"] as const satisfies readonly Channel[];

export function isMetaChannel(c: Channel): boolean {
  return (META_CHANNELS as readonly Channel[]).includes(c);
}

/**
 * Whether the 24h messaging window applies. Use this, not `isMetaChannel`, for
 * anything about whether a message may be sent: the two were the same set
 * until comments existed and are not the same question.
 */
export function isWindowGated(c: Channel): boolean {
  return (WINDOW_GATED_CHANNELS as readonly Channel[]).includes(c);
}

/**
 * Outcome labels. "the whole ballgame", Stage 3 / B3.
 *
 * TA-340: declared once, as a tuple, the way `MEMORY_STATUSES` is, and
 * `Outcome` is derived from it. packages/db's `outcome_label` pgEnum is built
 * from this array and packages/pipeline's label cache and LLM labeler read
 * it, where each used to keep its own copy. The order is load-bearing twice:
 * it is migration 0000's declaration order, which is how Postgres sorts an
 * enum, and the labeler's prompt lists the labels by joining it while the
 * label cache records no prompt hash, so a reorder would change every
 * labeling prompt without a trace. packages/db/src/enumTuples.test.ts holds
 * it to the migrations. Adding a value here still needs a migration.
 */
export const OUTCOMES = ["sold", "booked", "deposit", "ghosted", "lost", "ongoing"] as const;
export type Outcome = (typeof OUTCOMES)[number];

/**
 * The outcomes that end a thread for the follow-up nudge: a sale made, a
 * lead gone quiet past the ghost line, a lead lost. `booked`, `deposit` and
 * `ongoing` settle nothing. Written once (production-loop item SM, the RV rec
 * "Terminal outcome set exported once from core", accepted in Kabir's typed
 * reply of 2026-10-03), where `planFollowUp`, the follow-up sweep's SQL
 * prefilter and leadQuality's closing set each used to keep a copy. The order
 * is the order the sweep's SQL text lists them in, which an outcomes CLI test
 * has always pinned, so building that text from this tuple changes nothing.
 * It is a subset of `OUTCOMES` and does not reorder it. The outcomes CLI's own
 * `CLOSING_LABELS` (sold, lost) is the two an owner types, a different set on
 * purpose and not this one.
 */
export const TERMINAL_OUTCOMES = ["sold", "ghosted", "lost"] as const satisfies readonly Outcome[];

/** Whether a label ends a thread for the nudge. Null, undefined and any other string are not terminal. */
export function isTerminalOutcome(label: Outcome | string | null | undefined): boolean {
  return (TERMINAL_OUTCOMES as readonly string[]).includes(label as string);
}

/**
 * Appointment states (Agent Brains section 5.4). The charter names three,
 * verbatim: "Consults booked, confirmed, and attended." `no_show` and
 * `cancelled` are the two obvious failure cases the mission line does not
 * name but every calendar needs: an appointment that reached its scheduled
 * moment and nobody came, and one that was called off before it did.
 *
 * Deliberately its own type, not folded into `Outcome`. An outcome label is a
 * deal-stage fact about a thread (sold, lost, still open); an appointment
 * status is a milestone fact about one specific booking, and a thread can
 * carry several appointments (a consult that no-shows, then rebooks and is
 * attended) without that ever being several outcomes. See FINDINGS.md TA-185.
 */
export type AppointmentStatus = "booked" | "confirmed" | "attended" | "no_show" | "cancelled";

/**
 * Inquiry types, Stage 3.
 *
 * TA-340: one tuple, for the same reasons and under the same order rule as
 * `OUTCOMES` above. packages/db's `inquiry_type` pgEnum is built from it.
 */
export const INQUIRY_TYPES = ["repair", "custom", "ready_made", "price_check", "warranty", "dead"] as const;
export type InquiryType = (typeof INQUIRY_TYPES)[number];

/** Thread tiers, Stage 2. A = complete arc, B = fragment, C = voice-only. */
export type ThreadTier = "A" | "B" | "C";

/**
 * Evidence kinds (B2). A fact is priced by the KIND of observation that
 * produced it, deterministically. The model never self-grades a fact.
 */
export type EvidenceKind =
  | "dm.stated-budget"
  | "dm.customer-photo"
  | "dm.customer-claim"
  | "payment.deposit-paid"
  | "ig.bio-claim"
  | "consult.attended"
  | "owner.authored"
  /**
   * TA-190: a fact surfaced by a web search, not authored by the shop, the
   * customer, or observed directly. Scoped to `contact_memory` rows, the
   * only place `EvidenceKind` is a field at all (confirmed by grep before
   * adding this: it does not exist on a KB chunk, `packages/pipeline/src/
   * types.ts`'s `KbChunk`). Additive only: nothing writes this kind to a
   * `contact_memory` row today (no charter agent is wired to the web-search
   * tool, see `webSearchTool.ts`), and the Postgres `evidence_kind` enum
   * (`packages/db/src/schema.ts`) is deliberately left untouched until that
   * write path exists, so a migration lands with the feature that needs it
   * rather than speculatively ahead of it. AWAITING RATIFICATION, see
   * FINDINGS.md TA-190.
   */
  | "web.search-result";

/**
 * Deterministic trust price per evidence kind. NOT model-produced.
 * `dm.customer-claim` is deliberately near-worthless: it is the vector for the
 * stored-and-replayed memory-poisoning attack (B2).
 *
 * Two entries are RATIFIED by Kabir 2026-10-02 (decision list item 141, typed
 * reply "Yes I want to continue the production" to the accept-the-recs
 * question): `dm.stated-budget` at 0.7, which the budget saver files under,
 * and `dm.customer-claim` at 0.1, which ring size, metal and key date file
 * under. The 0.1 is revisited after a shadow week shows how often those facts
 * are right. The rest of that ruling (the four savers and the caps) is in
 * memorySaveGate.ts. Nothing else in this table is part of it.
 *
 * `web.search-result` (TA-190) is priced below every human-sourced kind,
 * including the customer's own Instagram bio (`ig.bio-claim`): a search
 * result is neither authored by this shop nor stated by the person it is
 * about, and it arrives already screened for injection and quarantined
 * (`webSearchQuarantine.ts`) precisely because it is the least trustworthy
 * kind of text this system reads. 0.2 is a placeholder judgment call, not a
 * measured figure: AWAITING RATIFICATION, FINDINGS.md TA-190.
 */
export const EVIDENCE_WEIGHT: Record<EvidenceKind, number> = {
  "owner.authored": 1.0,
  "payment.deposit-paid": 0.95,
  "consult.attended": 0.9,
  "dm.stated-budget": 0.7,
  "dm.customer-photo": 0.6,
  "ig.bio-claim": 0.4,
  "web.search-result": 0.2,
  "dm.customer-claim": 0.1,
};

/** The three memory layers (B2). Never blurred. */
export type MemoryLayer = "history" | "memory" | "kb";

/**
 * A `contact_memory` row's lifecycle (B2, section 6), declared once
 * (core-duplication/memory-status-union-declared-twice). `packages/db`'s
 * `memoryStatusEnum` is built from this tuple and its `MemoryStatus` is this
 * type, so the Postgres enum, the accessor and the lead card read one list.
 * Core owns it for the same reason it owns `MemoryLayer`: db depends on core,
 * never the reverse. Adding a value here still needs a migration.
 */
export const MEMORY_STATUSES = [
  "active",
  "held", // contradiction-holds: unresolved, surfaced to a human, never averaged
  "superseded",
  "pruned",
] as const;
export type MemoryStatus = (typeof MEMORY_STATUSES)[number];

/** Write tiers (B5). */
export type WriteTier = "T1" | "T2" | "T3";

/** Graduation classes (Agent Brains §9). D is never fully automated. */
export type GraduationClass = "A" | "B" | "C" | "D";

/** Agent identity, stamped on every gateway call and every event row. */
export interface AgentIdentity {
  readonly agentId: string;
  readonly agentVersion: string;
}
